import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import { camelCase } from 'lodash/string';
import numeral from 'numeral';

import {
  BAR_CHART_REPORTS,
  getReportConfigProp,
  REPORT_CATEGORY_OPTIONS,
} from '@/app/dashboards/utils/reports.enum';
import constants from '@/app/settings/components/CustomMetrics/constants';
import { toolTips } from '@/config';
import { ADS_REPORT_AGGREGATE_BY_TYPES } from '@/models/dashboards/aggregate-type.enum.js';
import { ADS_CHANNELS, CHANNELS } from '@/models/dashboards/channels.enum';
import { GRAPH_SCALES } from '@/models/dashboards/graph-scales.enum';
import { GRAPH_STATS } from '@/models/dashboards/graph-stats.enum';
import {
  DURATION_FORMATS,
  POST_TYPES,
  UPDATED_CONFIG_CHANNELS,
} from '@/models/dashboards/metrics.constants';
import { getEnumValue } from '@/models/enum.utils';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { formatDuration, formatDynamicDuration } from '@/utils/formatters';
import { getMetricDetails } from '@/utils/metrics';

// TODO 104187: Remove this file and all references. Consts should be moved to consts file.
export const METRICS = {};

const UNAVAILABLE_METRICS_FALLBACK = {};
const multiChannelTooltips = {
  ...toolTips.multiChannelDashboard,
  ...toolTips.sentimentDashboard,
};

export function isUnavailableMetricByFlags(channel, metric, flags) {
  if (flags) {
    return (flags.dashboardUnavailableMetrics?.[channel] || []).includes(metric);
  }
  return (UNAVAILABLE_METRICS_FALLBACK[channel] || []).includes(metric);
}

export const DEFAULT_FORMAT = Object.freeze({
  NORMAL: {
    value: 'NORMAL',
    format: '0.[00]a',
  },
  LONG: {
    value: 'LONG',
    format: '0,0.[00]',
  },
});

export function getMetricConfigProp(channel, metric, property, defaultValue) {
  const channelKey = getEnumValue(channel);
  const metricKey = getEnumValue(metric);
  return get(METRICS, `${channelKey}.${metricKey}.${property}`, defaultValue);
}

export function getMediaV2MetricName(channel, metric) {
  const mediaV2MetricName = getMetricConfigProp(channel, metric, 'mediaV2MetricName');
  const metricValue = getEnumValue(metric);
  const defaultMediaV2tMetricName = metricValue?.toLowerCase();
  return mediaV2MetricName || defaultMediaV2tMetricName || metricValue;
}

export function isMultiChannel(channels) {
  return Array.isArray(channels) && channels.length > 1;
}

export function getMetricText(
  firstChannel,
  metric,
  allChannels = null,
  report = null,
  aggregatedBy = '',
  metricReportTypeKey = null,
) {
  const metricTypeReportKey =
    metricReportTypeKey ?? getReportConfigProp(report, 'metricTypeReportKey');
  // get displayName from back end data if available
  const channels = allChannels ?? [firstChannel];
  let { displayName: name } = getMetricDetails(metric, channels, metricTypeReportKey);
  if (!ADS_CHANNELS.includes(firstChannel)) {
    return name;
  }
  if (
    [CHANNELS.META_ADS.value, CHANNELS.TIKTOK_ADS.value].includes(firstChannel) &&
    name &&
    aggregatedBy
  ) {
    name = name.replace('- Ads', `${ADS_REPORT_AGGREGATE_BY_TYPES[aggregatedBy].text} - Ads`);
  }
  return name;
}

function getMetricFormat(channel, metric, type = DEFAULT_FORMAT.NORMAL) {
  const formatType = getEnumValue(type);
  const metricFormats = getMetricConfigProp(channel, metric, 'formats');
  const format = metricFormats?.[formatType];
  return format || DEFAULT_FORMAT[formatType]?.format || DEFAULT_FORMAT.NORMAL.format;
}

export function getMetricTooltip(channels, metric) {
  if (isMultiChannel(channels)) {
    return get(multiChannelTooltips, camelCase(metric), '');
  }
  const firstChannel = [].concat(channels || [])?.[0];
  return getMetricConfigProp(firstChannel, metric, 'tooltip') || '';
}

export function getMetricGraphScale(channel, metric) {
  return getMetricConfigProp(channel, metric, 'graphScales') || Object.keys(GRAPH_SCALES);
}

export function getMetricGraphStats(
  channel,
  metric,
  reportType = null,
  metricType = null,
  isCustomMetric = false,
) {
  if (metricType === constants.METRIC_TYPE.PERCENTAGE.value && isCustomMetric) {
    return [];
  }
  if (BAR_CHART_REPORTS.includes(reportType)) {
    return [GRAPH_STATS.SUM.value];
  }
  return (
    getMetricConfigProp(channel, metric, 'graphStats') || [
      GRAPH_STATS.SUM.value,
      GRAPH_STATS.MEAN_BY_DAY.value,
    ]
  );
}

function formatValue(value, format = DEFAULT_FORMAT.NORMAL.format, emptyValue = undefined) {
  switch (format) {
    case DURATION_FORMATS.HOURS:
      return formatDuration(value, 0, 'hour');
    case DURATION_FORMATS.HOURS_MINUTES:
      return formatDuration(value, 0, 'minute');
    case DURATION_FORMATS.HOURS_MINUTES_SECONDS:
      return formatDuration(value, value >= 60 ? 0 : 1);
    case DURATION_FORMATS.SHORTENED_YEARS_DAYS_HOURS_MINUTES_SECONDS:
      return formatDynamicDuration(value);
    default:
      return isNumber(value) ? numeral(value).format(format).toUpperCase() : emptyValue;
  }
}

export function formatMetric(channel, metric, value, type, unit = '') {
  let metricKey = metric;
  const dashboardReportsStore = useDashboardReportsStore();
  const channelHasMetricConfig = METRICS?.[channel]?.[metric];
  if (!channelHasMetricConfig) {
    metricKey = dashboardReportsStore.getComparableMetricBasedOnChannel(channel, metric);
  }
  const emptyValue =
    METRICS?.[channel]?.[metricKey] &&
    Object.keys(METRICS?.[channel]?.[metricKey]).includes('emptyValue')
      ? String(METRICS?.[channel]?.[metricKey].emptyValue)
      : '-';
  let formattedValue = formatValue(value, getMetricFormat(channel, metricKey, type), emptyValue);
  if (unit) {
    formattedValue = formattedValue.concat(` ${unit}`);
  }
  return formattedValue;
}

// TODO 104187: Filter functions should be simplified and moved into metrics util file
export function filterMetricsByBrandPermissions(channels, brands = null) {
  const identityStore = useIdentityStore();
  return (metric) =>
    channels.every((channel) =>
      identityStore.checkIfBrandPermissionsExist(METRICS?.[channel]?.[metric]?.permissions, brands),
    );
}

export function filterMetricsIfHidden(channels, allMetricDetails, metricTypeReportKey) {
  return (metric) =>
    channels.every((channel) => {
      const flagStore = useFlagStore();
      const dashboardReportsStore = useDashboardReportsStore();

      const isMultipleChannels = channels.length > 1;
      const comparableMetricKey = isMultipleChannels
        ? dashboardReportsStore.comparableMetrics?.[metric]?.[channel]
        : null;
      const metricDetails =
        allMetricDetails?.[channel]?.[metricTypeReportKey]?.[comparableMetricKey ?? metric];
      const requiredFeatureFlag = metricDetails?.required_feature_flag;

      if (isMultipleChannels) {
        const comparableRequiredFeatureFlag =
          allMetricDetails?.MULTI_CHANNEL?.[metric]?.required_feature_flag;
        const hasRequiredComparableFlag = Boolean(
          !comparableRequiredFeatureFlag ||
            (flagStore.ready && flagStore.flags?.[comparableRequiredFeatureFlag]),
        );
        if (!hasRequiredComparableFlag) return false;
      }

      const hasRequiredFlag = Boolean(
        !requiredFeatureFlag || (flagStore.ready && flagStore.flags?.[requiredFeatureFlag]),
      );

      return !metricDetails?.hide_on_dashboards && hasRequiredFlag;
    });
}

export function filterMetricByDisabledMultiChannel(channels) {
  return (metric) => {
    if (channels.length === 1) {
      return true;
    }
    const channelMetricDisabledMap = channels.map(
      (channel) => METRICS?.[channel]?.[metric]?.disableMultiChannelGraph === true,
    );
    const channelMetricDisabled = channelMetricDisabledMap.includes(true);
    return !channelMetricDisabled;
  };
}

export function filterMetricIfUnknownInChannel(
  channels,
  comparableMetrics,
  allMetricDetails,
  metricTypeReportKey,
) {
  const isMultiChannelReport = channels.length > 1;
  return (metric) =>
    channels.every((channel) => {
      const mappedMetric = isMultiChannelReport ? comparableMetrics?.[metric]?.[channel] : metric;

      if (UPDATED_CONFIG_CHANNELS.includes(channel)) {
        if (
          channel === CHANNELS.INSTAGRAM.value &&
          ![
            'AVG_EFFECTIVENESS_V3',
            'IMPRESSIONS_V2',
            'TOTAL_VIDEO_VIEWS_V2',
            'ORGANIC_VIDEO_VIEWS_V2',
          ].includes(metric)
        ) {
          return (
            allMetricDetails?.[channel]?.[metricTypeReportKey]?.[mappedMetric] ||
            allMetricDetails?.[CHANNELS.INSTAGRAM_UGC.value]?.[metricTypeReportKey]?.[mappedMetric]
          );
        }
        return allMetricDetails?.[channel]?.[metricTypeReportKey]?.[mappedMetric];
      }
      if (channel === CHANNELS.INSTAGRAM.value) {
        return (
          METRICS?.[CHANNELS.INSTAGRAM.value]?.[mappedMetric] ||
          METRICS?.[CHANNELS.INSTAGRAM_UGC.value]?.[mappedMetric]
        );
      }
      return METRICS?.[channel]?.[mappedMetric];
    });
}

export function metricSupportsContentTags(channel, metrics, report, allMetricDetails) {
  const metricTypeReportKey = getReportConfigProp(report, 'metricTypeReportKey');

  return []
    .concat(metrics ?? [])
    .every(
      (metric) => allMetricDetails?.[channel]?.[metricTypeReportKey]?.[metric]?.allow_content_tags,
    );
}

export function filterMetricsByDataSource(
  channels,
  allMetricDetails,
  comparableMetrics,
  metricTypeReportKey,
  reportCategory,
) {
  return (metric) => {
    const isMultiChannelReport = channels.length > 1;

    if (!reportCategory) {
      return true;
    }

    const categoryConfig = Object.values(REPORT_CATEGORY_OPTIONS).find(
      (category) => category?.id === reportCategory,
    );

    if (!categoryConfig?.dataSources) {
      return true;
    }

    return channels.every((channel) => {
      const mappedMetric = isMultiChannelReport ? comparableMetrics?.[metric]?.[channel] : metric;
      const metricDetails = allMetricDetails?.[channel]?.[metricTypeReportKey]?.[mappedMetric];
      const dataSource = metricDetails?.data_source;

      return categoryConfig.dataSources.includes(dataSource);
    });
  };
}

export function metricRequiresAdAccount(metricDetails, channel = null) {
  const postTypes = metricDetails?.post_types ?? [];
  const timeframe = metricDetails?.timeframe ?? '';
  if (postTypes.length === 0) return false;

  // Promoted-only or ADS-only metrics require ad account for all channels
  const isPromotedOrAdsOnly = postTypes.every((postType) =>
    [POST_TYPES.PROMOTED, POST_TYPES.ADS].includes(postType),
  );
  if (isPromotedOrAdsOnly) return true;

  const isEq = (postTypes1, postTypes2) => isEqual(new Set(postTypes1), new Set(postTypes2));
  if (channel === CHANNELS.TIKTOK.value) {
    return (
      isEq(postTypes, [POST_TYPES.ORGANIC]) ||
      isEq(postTypes, [POST_TYPES.PROMOTED]) ||
      isEq(postTypes, [POST_TYPES.ESTIMATED_ORGANIC])
    );
  }

  if (channel === CHANNELS.FACEBOOK.value) {
    return isEq(postTypes, [POST_TYPES.ORGANIC]) || isEq(postTypes, [POST_TYPES.PROMOTED]);
  }

  if (channel === CHANNELS.INSTAGRAM.value) {
    return (
      isEq(postTypes, [POST_TYPES.PROMOTED]) ||
      isEq(postTypes, [POST_TYPES.ORGANIC, POST_TYPES.PROMOTED])
    );
  }

  return false;
}

export function metricIsOrganicAndPromoted(metricDetails) {
  const postTypes = metricDetails?.post_types ?? [];
  const isEq = (a, b) => isEqual(new Set(a), new Set(b));
  return isEq(postTypes, [POST_TYPES.ORGANIC, POST_TYPES.PROMOTED]);
}

export function filterMetricsByAdAccountConnection(
  channels,
  allMetricDetails,
  comparableMetrics,
  metricTypeReportKey,
  hasAdAccountForChannel,
) {
  return (metric) => {
    const isMultiChannelReport = channels.length > 1;

    return channels.every((channel) => {
      const mappedMetric = isMultiChannelReport ? comparableMetrics?.[metric]?.[channel] : metric;
      const metricDetails = allMetricDetails?.[channel]?.[metricTypeReportKey]?.[mappedMetric];

      const requiresAdAccount = metricRequiresAdAccount(metricDetails, channel);
      if (!requiresAdAccount) return true;

      return hasAdAccountForChannel(channel);
    });
  };
}

export function filterMetricsIfContentTags(
  channels,
  contentTagIds,
  report,
  allMetricDetails,
  isReportForm,
) {
  const dashboardReportsStore = useDashboardReportsStore();
  const hasContentTags = contentTagIds?.length > 0;

  return (metric) => {
    if (!hasContentTags || isReportForm) return true;
    return channels.every((channel) => {
      const comparableMetric =
        channels.length > 1
          ? dashboardReportsStore.getComparableMetricBasedOnChannel(channel, metric)
          : metric;

      return metricSupportsContentTags(channel, comparableMetric, report, allMetricDetails);
    });
  };
}
